import { useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useDateNow, useDispatch, useReduxState } from 'hooks';
import { addDays, format } from 'date-fns';
import api from 'api';
import { Alert, ModalButtonWithForm } from 'components/common';
import { Box } from 'components/common/box';
import {
  Checkbox,
  DatepickerWithTime,
  FormGroup,
  Text
} from 'components/common/form';

import type { FormApi } from 'informed';

type FormState = Record<'title', string> &
  Record<'endDate' | 'lastJoinDate' | 'startDate', number> &
  Record<'copyAllOwners', boolean>;

const CalendarEventCopy = () => {
  const { dateNow } = useDateNow();

  const { dispatch } = useDispatch();

  const {
    messages,
    pageState: { calendarEvent }
  } = useReduxState();

  const formApi = useRef<FormApi<FormState>>(undefined);

  const router = useRouter();

  const tomorrowMorning = new Date(
    `${format(addDays(dateNow, 1), 'yyyy-MM-dd')}T08:00:00`
  ).getTime();

  const [startDate, setStartDate] = useState<number>(tomorrowMorning);

  const handleStartDateChange = (value: number) => {
    const timeDiff = value - startDate;

    const endDate: number = formApi.current.getValue('endDate');
    formApi.current.setValue('endDate', endDate + timeDiff);

    const lastJoinDate = formApi.current.getValue('lastJoinDate') as number;
    if (lastJoinDate > value) formApi.current.setValue('lastJoinDate', value);

    setStartDate(value);
  };

  const handleSubmit = async (values: FormState) => {
    const startDate = new Date(values.startDate).toISOString();
    const endDate = new Date(values.endDate).toISOString();
    const lastJoinDate = values.lastJoinDate
      ? new Date(values.lastJoinDate).toISOString()
      : null;

    try {
      const response = await api.post(
        `/admin/calendar-events/${calendarEvent.id}/copy`,
        {
          title: values.title,
          startDate,
          endDate,
          lastJoinDate,
          copyAllOwners: values.copyAllOwners
        }
      );

      if (response.data?.success) {
        dispatch.addNotice({
          text: messages.eventCalendarCopySuccess,
          type: 'success'
        });
        router.push(`/calendar/${response.data.eventId}/settings`);
      } else {
        throw new Error('Something went wrong...');
      }
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
    }
  };

  const validateTitle = (value: string) => {
    return !value?.trim() ? messages.commonTitleEmpty : undefined;
  };

  return (
    <Box title={messages.eventCalendarCopy}>
      <Alert
        style={{ marginBottom: 10 }}
        text={messages.eventCalendarCopyInfo}
      />

      <ModalButtonWithForm
        buttonIcon="copy"
        buttonText={messages.commonCopy}
        buttonCategory="success"
        modalActionText={messages.commonConfirm}
        modalTitle={messages.eventCalendarCopy}
        onSubmit={handleSubmit}
        pristineIsValid={false}
        setFormApi={(api: FormApi<FormState>) => (formApi.current = api)}
      >
        {({ values }: { values: FormState }) => (
          <>
            <FormGroup label={messages.commonTitle}>
              <Text
                field="title"
                id="title"
                required={true}
                validate={validateTitle}
                validateOnChange={true}
              />
            </FormGroup>

            <FormGroup label={messages.publishStartDate}>
              <DatepickerWithTime
                field="startDate"
                id="startDate"
                initialValue={tomorrowMorning}
                isClearable={false}
                minDate={dateNow}
                onChange={handleStartDateChange}
                usePortal={true}
              />
            </FormGroup>

            <FormGroup label={messages.publishEndDate}>
              <DatepickerWithTime
                field="endDate"
                id="endDate"
                initialValue={Date.parse(
                  `${format(tomorrowMorning, 'yyyy-MM-dd')}T10:00:00`
                )}
                isClearable={false}
                minDate={values.startDate}
                usePortal={true}
              />
            </FormGroup>

            <FormGroup
              label={messages.eventCalendarJoinLastDate}
              labelTooltip={messages.eventCalendarJoinLastDateInfo}
            >
              <DatepickerWithTime
                field="lastJoinDate"
                id="lastJoinDate"
                isClearable={true}
                maxDate={values.startDate}
                placeholder={messages.commonSetDate}
                style={{ maxWidth: 275 }}
                usePortal={true}
              />
            </FormGroup>

            <FormGroup label={messages.commonCopyOwners}>
              <Checkbox
                field="copyAllOwners"
                id="copyAllOwners"
                label={messages.eventCalendarCopyOwnersInfo}
              />
            </FormGroup>
          </>
        )}
      </ModalButtonWithForm>
    </Box>
  );
};

export default CalendarEventCopy;
